/**
 * DependencySelector component for adding/removing predecessor tasks.
 * 
 * Allows users to select tasks from the same project to add as dependencies.
 * Prevents circular dependencies and shows error messages when they occur.
 */

import React, { useState, useEffect } from 'react';
import { Task } from '../types/task';
import { TaskDependencyWithDetails, createDependency, deleteDependency, getTaskDependencies } from '../api/task-dependencies';
import Button from './Button';

interface DependencySelectorProps {
  task: Task;
  availableTasks: Task[]; // Tasks in the same project
  onDependencyAdded?: () => void;
  onDependencyRemoved?: () => void;
}

const DependencySelector: React.FC<DependencySelectorProps> = ({
  task,
  availableTasks,
  onDependencyAdded,
  onDependencyRemoved,
}) => {
  const [selectedPredecessorId, setSelectedPredecessorId] = useState<string>('');
  const [dependencies, setDependencies] = useState<TaskDependencyWithDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadDependencies = async () => {
    try {
      setError(null);
      const deps = await getTaskDependencies(task.id);
      // Filter to get only the dependencies where this task is the dependent
      const taskDeps = deps.filter(d => d.dependent_task_id === task.id);
      setDependencies(taskDeps);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dependencies';
      setError(errorMessage);
      setDependencies([]); // Set empty array on error to prevent crashes
    }
  };

  // Load existing dependencies when component mounts
  useEffect(() => {
    loadDependencies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  const handleAddDependency = async () => {
    if (!selectedPredecessorId) {
      setError('Please select a predecessor task');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await createDependency({
        dependent_task_id: task.id,
        predecessor_task_id: selectedPredecessorId,
      });

      setSuccessMessage('Dependency added successfully');
      setSelectedPredecessorId('');
      await loadDependencies(); // Reload dependencies
      
      if (onDependencyAdded) {
        onDependencyAdded();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add dependency';
      
      // Check for specific error messages
      if (errorMessage.includes('circular')) {
        setError('Cannot add dependency: This would create a circular dependency');
      } else if (errorMessage.includes('project')) {
        setError('Cannot add dependency: Tasks must be in the same project');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDependency = async (dependencyId: string) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await deleteDependency(dependencyId);
      setSuccessMessage('Dependency removed successfully');
      await loadDependencies(); // Reload dependencies
      
      if (onDependencyRemoved) {
        onDependencyRemoved();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove dependency';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter out current task and tasks that are already dependencies
  const existingPredecessorIds = dependencies.map(d => d.predecessor_task_id);
  const selectableTasks = availableTasks.filter(
    t => t.id !== task.id && !existingPredecessorIds.includes(t.id)
  );

  return (
    <div className="dependency-selector">
      <h4 className="text-md font-semibold mb-2 text-gray-900 dark:text-gray-100">Dependencies (Predecessors)</h4>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Tasks that must be completed before this task can start.
      </p>

      {/* Error alert */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 dark:border-red-700 rounded-lg text-red-900 dark:text-red-100 mb-4">
          {error}
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 dark:border-green-700 rounded-lg text-green-900 dark:text-green-100 mb-4">
          {successMessage}
        </div>
      )}

      {/* Existing dependencies list */}
      {dependencies.length > 0 && (
        <div className="mb-4">
          <h5 className="text-sm font-semibold mb-2 text-gray-900 dark:text-gray-100">Current Dependencies:</h5>
          <ul className="space-y-2">
            {dependencies.map(dep => (
              <li key={dep.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
                <span className="text-sm text-gray-900 dark:text-gray-100">
                  {dep.predecessor_task_title} ({dep.predecessor_task_status})
                </span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemoveDependency(dep.id)}
                  disabled={loading}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Add new dependency */}
      <div className="flex gap-2">
        <select
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 flex-1"
          value={selectedPredecessorId}
          onChange={(e) => setSelectedPredecessorId(e.target.value)}
          disabled={loading || selectableTasks.length === 0}
        >
          <option value="">Select a predecessor task...</option>
          {selectableTasks.map(t => (
            <option key={t.id} value={t.id}>
              {t.title} ({t.status})
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          onClick={handleAddDependency}
          disabled={loading || !selectedPredecessorId}
        >
          Add
        </Button>
      </div>

      {selectableTasks.length === 0 && dependencies.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          No other tasks available in this project to add as dependencies.
        </p>
      )}
    </div>
  );
};

export default DependencySelector;
